// Every page and its URL. Org pages live under /o/:orgSlug so an org can never clash with a page name. Pages load
// their code when first opened (lib/pages.ts); the frames around them (the session check, the org shell) come with
// the first download.

import { createBrowserRouter, Navigate, Outlet } from 'react-router'

import { Splash } from './components/Splash'
import { pages } from './lib/pages'
import { OrgLayout } from './routes/app/OrgLayout'
import { ErrorPage } from './routes/ErrorPage'
import { HomeRedirect } from './routes/HomeRedirect'
import { NotFoundPage } from './routes/NotFoundPage'
import { RequireSession } from './routes/RequireSession'

const SignedIn = () => (
  <RequireSession>
    <Outlet />
  </RequireSession>
)

export const router = createBrowserRouter([
  {
    // Shown while the first page's code arrives; later pages load while the current one stays on screen.
    HydrateFallback: Splash,
    // Anything that breaks outside an org's shell: the error page on its own (a page in an org keeps the sidebar).
    errorElement: <ErrorPage />,
    children: [
      { path: '/', element: <HomeRedirect /> },
      { path: '/login', lazy: pages.login },
      { path: '/signup', lazy: pages.signup },
      { path: '/check-email', lazy: pages.checkEmail },
      { path: '/verify-email', lazy: pages.verifyEmail },
      // OAuth consent: handles signed-out people itself (log in or sign up, then back here).
      { path: '/oauth/authorize', lazy: pages.authorize },
      { path: '/forgot-password', lazy: pages.forgotPassword },
      { path: '/reset-password', lazy: pages.resetPassword },
      { path: '/invite', lazy: pages.invite },
      { path: '/join', lazy: pages.join },
      // An alert email's unsubscribe link: works signed out (the link's token is the proof).
      { path: '/unsubscribe', lazy: pages.unsubscribe },
      {
        element: <SignedIn />,
        children: [
          { path: '/onboarding', lazy: pages.onboarding },
          // Another org, from the org switcher (a first org is onboarding's first step).
          { path: '/orgs/new', lazy: pages.newOrg },
          // The person's own account (Phase 16): outside any org.
          {
            path: '/account',
            lazy: pages.accountLayout,
            children: [
              { index: true, lazy: pages.account },
              { path: 'alerts', lazy: pages.myAlerts },
            ],
          },
          {
            path: '/o/:orgSlug',
            element: <OrgLayout />,
            // The shell itself breaking: the error page on its own.
            errorElement: <ErrorPage />,
            children: [
              {
                // A page that breaks shows the error page where the page was, the sidebar still there.
                errorElement: <ErrorPage isInShell />,
                children: [
                  { index: true, element: <Navigate to="home" replace /> },
                  { path: 'home', lazy: pages.home },
                  { path: 'calls', lazy: pages.calls },
                  { path: 'calls/:callId', lazy: pages.calls },
                  { path: 'contacts', lazy: pages.contacts },
                  { path: 'contacts/:contactId', lazy: pages.contacts },
                  { path: 'orders', lazy: pages.orders },
                  { path: 'orders/:orderId', lazy: pages.orders },
                  { path: 'flows', lazy: pages.flows },
                  { path: 'flows/library/:libraryKey', lazy: pages.libraryFlow },
                  { path: 'flows/:flowId', lazy: pages.flowEditor },
                  { path: 'results', lazy: pages.results },
                  { path: 'analytics', lazy: pages.analytics },
                  { path: 'stores', lazy: pages.stores },
                  { path: 'stores/:storeId', lazy: pages.store },
                  { path: 'integrations', lazy: pages.integrations },
                  { path: 'integrations/:appId', lazy: pages.integrations },
                  { path: 'billing/invoices/:invoiceId', lazy: pages.invoice },
                  { path: 'developers', lazy: pages.developers },
                  { path: 'business', lazy: pages.business },
                  // Legacy: the account was inside an org until Phase 16; it's the person's own now.
                  { path: 'account/*', element: <Navigate to="/account" replace /> },
                  {
                    path: 'settings',
                    lazy: pages.settings,
                    children: [
                      { index: true, lazy: pages.generalSettings },
                      // Legacy: Settings → Setup (the "what you need" questions) was removed on 2026-10-14.
                      { path: 'setup', element: <Navigate to=".." replace /> },
                      { path: 'voices', lazy: pages.voicesSettings },
                      { path: 'sop', lazy: pages.sopSettings },
                      { path: 'phone-numbers', lazy: pages.phoneNumbersSettings },
                      { path: 'members', lazy: pages.membersSettings },
                      { path: 'roles', lazy: pages.rolesSettings },
                      { path: 'audit', lazy: pages.auditSettings },
                      { path: 'connected-apps', lazy: pages.connectedAppsSettings },
                      { path: 'billing', lazy: pages.billingSettings },
                      { path: 'alerts', lazy: pages.alertsSettings },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: '/admin',
            lazy: pages.admin,
            children: [
              { index: true, lazy: pages.adminOrgs },
              { path: 'orgs/:orgId', lazy: pages.adminOrg },
              { path: 'calls', lazy: pages.adminCalls },
              { path: 'calls/:callId', lazy: pages.adminCall },
              { path: 'numbers', lazy: pages.adminNumbers },
              { path: 'waitlist', lazy: pages.adminWaitlist },
              { path: 'invites', lazy: pages.adminInvites },
              { path: 'alerts', lazy: pages.adminAlerts },
              { path: 'stores', lazy: pages.adminStores },
              { path: 'approvals', lazy: pages.adminApprovals },
              { path: 'approvals/:orgId', lazy: pages.adminApproval },
              { path: 'users', lazy: pages.adminUsers },
              { path: 'platforms', lazy: pages.adminPlatforms },
              { path: 'apps', lazy: pages.adminApps },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
